/**
 * Crossover-athlete detection helpers.
 *
 * A "crossover" athlete is one assigned to more than one team within the same
 * organization (a common all-star cheer scenario where an athlete competes on
 * multiple teams). These helpers answer "is this athlete a crossover?" and
 * "how many / which teams?" — each preferring a server-side RPC and falling
 * back to a manual team-count query when the RPC is unavailable.
 *
 * Extracted verbatim from `organizationScope.ts` (no behavior change) to keep
 * that module under the 400-line `max-lines` cap enforced on `utils/**`. All
 * public functions remain importable from `@/utils/supabase/organizationScope`
 * via a re-export there, so existing import paths are unchanged. New code may
 * import directly from this module.
 *
 * Added: 2025-06-21 - Functions for crossover vs shared athlete detection
 * Documentation: /docs/prd/technical/DataAccessPatterns.md
 */

import { supabase } from './client';
import { createLogger } from '@/utils/logger/devLogger';
// Org-membership predicate lives in its own leaf module. Importing it from there
// (rather than from ./organizationScope, which re-exports this module) keeps the
// dependency graph acyclic — required because the failing test spreads
// jest.requireActual('@/utils/supabase/organizationScope').
import { isAthleteInOrganization } from './athleteOrganizationMembership';

// Log under the original 'organizationScope' namespace so these helpers' log
// output is byte-identical to before the extraction (pure refactor).
const logger = createLogger('organizationScope');

/**
 * Checks if an athlete is a crossover athlete within an organization
 *
 * @param athleteId The athlete ID to check
 * @param organizationId The organization ID
 * @returns Promise resolving to true if athlete is crossover
 */
export async function isAthleteCrossoverInOrganization(
  athleteId: string,
  organizationId: string
): Promise<boolean> {
  try {
    // First, verify athlete belongs to this organization
    const belongsToOrg = await isAthleteInOrganization(athleteId, organizationId);
    if (!belongsToOrg) {
      return false;
    }

    // Try to call the RPC function
    const { data, error } = await supabase.rpc('is_athlete_crossover_in_organization', {
      p_athlete_id: athleteId,
      p_organization_id: organizationId
    });

    if (error) {
      logger.warn('RPC function failed for crossover check, falling back to manual calculation:', error);
      // Fallback to manual team count
      return await fallbackCrossoverCheck(athleteId, organizationId);
    }

    return data || false;
  } catch (error) {
    logger.warn('Exception calling RPC function for crossover check, falling back to manual calculation:', error);
    // Fallback to manual team count
    return await fallbackCrossoverCheck(athleteId, organizationId);
  }
}

/**
 * Fallback crossover check when RPC function is not available
 */
async function fallbackCrossoverCheck(
  athleteId: string,
  organizationId: string
): Promise<boolean> {
  try {
    // First get team IDs for this organization
    const { data: teams, error: teamsError } = await supabase
      .from('teams')
      .select('id')
      .eq('organization_id', organizationId);

    if (teamsError || !teams || teams.length === 0) {
      return false;
    }

    const teamIds = teams.map(team => team.id);

    // Count active team assignments for this athlete within these teams
    const { count, error } = await supabase
      .from('team_athletes')
      .select('team_id', { count: 'exact', head: true })
      .eq('athlete_id', athleteId)
      .eq('status', 'active')
      .in('team_id', teamIds);

    if (error) {
      logger.error('Error in fallback crossover check:', error);
      return false;
    }

    return (count || 0) > 1;
  } catch (error) {
    logger.error('Exception in fallback crossover check:', error);
    return false;
  }
}

/**
 * Gets all crossover athletes in an organization
 *
 * @param organizationId The organization ID
 * @returns Promise with array of crossover athletes and their details
 */
export async function getCrossoverAthletesInOrganization(
  organizationId: string
): Promise<{ data: any[]; error: any }> {
  try {
    // Try to call the RPC function
    const { data, error } = await supabase.rpc('get_crossover_athletes_in_organization', {
      p_organization_id: organizationId
    });

    if (error) {
      logger.warn('RPC function failed for getting crossover athletes, falling back to manual calculation:', error);
      // Fallback to manual calculation
      return await fallbackGetCrossoverAthletes(organizationId);
    }

    return { data: data || [], error: null };
  } catch (error) {
    logger.warn('Exception calling RPC function for getting crossover athletes, falling back to manual calculation:', error);
    // Fallback to manual calculation
    return await fallbackGetCrossoverAthletes(organizationId);
  }
}

/**
 * Fallback to get crossover athletes when RPC function is not available
 */
async function fallbackGetCrossoverAthletes(
  organizationId: string
): Promise<{ data: any[]; error: any }> {
  try {
    // Get all athletes in the organization with their team assignments
    const { data: athleteTeams, error: queryError } = await supabase
      .from('team_athletes')
      .select(`
        athlete_id,
        is_primary_team,
        teams!inner(
          id,
          name,
          organization_id
        ),
        athletes!inner(
          id,
          first_name,
          last_name
        )
      `)
      .eq('teams.organization_id', organizationId)
      .eq('status', 'active');

    if (queryError) {
      logger.error('Error in fallback get crossover athletes:', queryError);
      return { data: [], error: queryError };
    }

    // Group by athlete and count teams
    const athleteTeamMap = new Map<string, {
      athlete: any;
      teamCount: number;
      teamNames: string[];
      primaryTeam: string | null;
    }>();

    athleteTeams?.forEach(assignment => {
      const athleteId = assignment.athlete_id;
      const teamName = (assignment.teams as any).name;
      const isPrimary = assignment.is_primary_team;
      const athlete = assignment.athletes;

      if (!athleteTeamMap.has(athleteId)) {
        athleteTeamMap.set(athleteId, {
          athlete,
          teamCount: 0,
          teamNames: [],
          primaryTeam: null
        });
      }

      const entry = athleteTeamMap.get(athleteId)!;
      entry.teamCount++;
      entry.teamNames.push(teamName);
      if (isPrimary) {
        entry.primaryTeam = teamName;
      }
    });

    // Filter for crossover athletes (more than 1 team)
    const crossoverAthletes = Array.from(athleteTeamMap.entries())
      .filter(([_, data]) => data.teamCount > 1)
      .map(([athleteId, data]) => ({
        athlete_id: athleteId,
        athlete_name: `${data.athlete.first_name} ${data.athlete.last_name}`,
        team_count: data.teamCount,
        team_names: data.teamNames,
        primary_team: data.primaryTeam
      }))
      .sort((a, b) => a.athlete_name.localeCompare(b.athlete_name));

    return { data: crossoverAthletes, error: null };
  } catch (error) {
    logger.error('Exception in fallback get crossover athletes:', error);
    return { data: [], error };
  }
}

/**
 * Gets crossover details for an athlete within an organization
 *
 * @param athleteId The athlete ID to check
 * @param organizationId The organization ID
 * @returns Promise with crossover details including team count and names
 */
export async function getAthleteCrossoverDetails(
  athleteId: string,
  organizationId: string
): Promise<{ data: any; error: any }> {
  try {
    // First, verify athlete belongs to this organization
    const belongsToOrg = await isAthleteInOrganization(athleteId, organizationId);
    if (!belongsToOrg) {
      return {
        data: {
          isCrossover: false,
          teamCount: 0,
          teamNames: [],
          primaryTeam: null
        },
        error: null
      };
    }

    // Try to call the RPC function
    const { data, error } = await supabase.rpc('get_athlete_crossover_details', {
      p_athlete_id: athleteId,
      p_organization_id: organizationId
    });

    if (error) {
      logger.warn('RPC function failed, falling back to manual calculation:', error);
      // Fallback to manual calculation
      return await fallbackCrossoverCalculation(athleteId, organizationId);
    }

    return { data, error: null };
  } catch (error) {
    logger.warn('Exception calling RPC function, falling back to manual calculation:', error);
    // Fallback to manual calculation
    return await fallbackCrossoverCalculation(athleteId, organizationId);
  }
}

/**
 * Fallback crossover calculation when RPC function is not available
 */
async function fallbackCrossoverCalculation(
  athleteId: string,
  organizationId: string
): Promise<{ data: any; error: any }> {
  try {
    // Get team assignments for this athlete within the organization
    const { data: teamAssignments, error: assignmentsError } = await supabase
      .from('team_athletes')
      .select(`
        team_id,
        is_primary_team,
        teams!inner(
          id,
          name,
          organization_id
        )
      `)
      .eq('athlete_id', athleteId)
      .eq('teams.organization_id', organizationId)
      .eq('status', 'active');

    if (assignmentsError) {
      logger.error('Error in fallback crossover calculation:', assignmentsError);
      return {
        data: {
          isCrossover: false,
          teamCount: 0,
          teamNames: [],
          primaryTeam: null
        },
        error: assignmentsError
      };
    }

    const teamCount = teamAssignments?.length || 0;
    const isCrossover = teamCount > 1;
    const teamNames = teamAssignments?.map(ta => (ta.teams as any).name) || [];
    const primaryTeamAssignment = teamAssignments?.find(ta => ta.is_primary_team);
    const primaryTeam = primaryTeamAssignment ? (primaryTeamAssignment.teams as any).name : null;

    return {
      data: {
        isCrossover,
        teamCount,
        teamNames,
        primaryTeam
      },
      error: null
    };
  } catch (error) {
    logger.error('Error in fallback crossover calculation:', error);
    return {
      data: {
        isCrossover: false,
        teamCount: 0,
        teamNames: [],
        primaryTeam: null
      },
      error
    };
  }
}

export interface CrossoverDetails {
  isCrossover: boolean;
  teamCount: number;
  teamNames: string[];
  primaryTeam: string | null;
}

/**
 * Batch fetch crossover details for multiple athletes in a single query
 * This is much more efficient than calling getAthleteCrossoverDetails for each athlete
 *
 * @param athleteIds Array of athlete IDs to check
 * @param organizationId The organization ID
 * @param seasonId Optional season scope. When a string is passed, only counts teams
 *   in that season. When `null` is passed, only counts teams whose `season_id IS NULL`
 *   in the DB (rare — legacy teams without a season). When omitted, counts teams
 *   across all seasons in the org — primary teams from ended seasons will leak
 *   into the result, so callers that display a season-specific roster should pass it.
 * @returns Promise with a map of athleteId -> crossover details
 */
export async function getBatchAthleteCrossoverDetails(
  athleteIds: string[],
  organizationId: string,
  seasonId?: string | null
): Promise<{ data: Record<string, CrossoverDetails>; error: Error | null }> {
  if (!athleteIds || athleteIds.length === 0) {
    return { data: {}, error: null };
  }

  try {
    logger.info(`Batch fetching crossover details for ${athleteIds.length} athletes`);

    // Fetch all team assignments for all athletes in a single query
    let assignmentsQuery = supabase
      .from('team_athletes')
      .select(`
        athlete_id,
        team_id,
        is_primary_team,
        teams!inner(
          id,
          name,
          organization_id,
          season_id
        )
      `)
      .in('athlete_id', athleteIds)
      .eq('teams.organization_id', organizationId)
      .eq('status', 'active');

    if (seasonId !== undefined) {
      assignmentsQuery = seasonId
        ? assignmentsQuery.eq('teams.season_id', seasonId)
        : assignmentsQuery.is('teams.season_id', null);
    }

    const { data: teamAssignments, error: assignmentsError } = await assignmentsQuery;

    const buildEmptyMap = (): Record<string, CrossoverDetails> => {
      const map: Record<string, CrossoverDetails> = {};
      athleteIds.forEach(id => {
        map[id] = { isCrossover: false, teamCount: 0, teamNames: [], primaryTeam: null };
      });
      return map;
    };

    if (assignmentsError) {
      logger.error('Error in batch crossover calculation:', assignmentsError);
      return { data: buildEmptyMap(), error: assignmentsError as Error };
    }

    // Group team assignments by athlete ID
    const assignmentsByAthlete = new Map<string, any[]>();
    (teamAssignments || []).forEach((assignment: any) => {
      const athleteId = assignment.athlete_id;
      if (!assignmentsByAthlete.has(athleteId)) {
        assignmentsByAthlete.set(athleteId, []);
      }
      assignmentsByAthlete.get(athleteId)!.push(assignment);
    });

    // Calculate crossover details for each athlete
    const result: Record<string, CrossoverDetails> = {};
    athleteIds.forEach(athleteId => {
      const assignments = assignmentsByAthlete.get(athleteId) || [];
      const teamCount = assignments.length;
      const isCrossover = teamCount > 1;
      const teamNames = assignments.map(ta => (ta.teams as any).name) || [];
      const primaryTeamAssignment = assignments.find(ta => ta.is_primary_team);
      const primaryTeam = primaryTeamAssignment ? (primaryTeamAssignment.teams as any).name : null;

      result[athleteId] = {
        isCrossover,
        teamCount,
        teamNames,
        primaryTeam
      };
    });

    logger.info(`Successfully calculated crossover details for ${Object.keys(result).length} athletes`);
    return { data: result, error: null };
  } catch (error) {
    logger.error('Error in batch crossover calculation:', error);
    const emptyMap: Record<string, CrossoverDetails> = {};
    athleteIds.forEach(id => {
      emptyMap[id] = { isCrossover: false, teamCount: 0, teamNames: [], primaryTeam: null };
    });
    return { data: emptyMap, error: error as Error };
  }
}
